import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Boxes, Plus, Pencil, Copy, Link2, Eye, Inbox, Power, Trash2, MapPin, DollarSign, Loader2,
} from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  draft: "bg-amber-100 text-amber-700 border-amber-200",
  inactive: "bg-slate-100 text-slate-600 border-slate-200",
};

const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : "—");
const publicUrl = (slug) => `${window.location.origin}/buybox/${slug}`;

export default function MyBuyBoxes() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/buybox");
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const copyLink = (bb) => {
    navigator.clipboard.writeText(publicUrl(bb.slug));
    toast.success("Public link copied");
  };
  const toggleStatus = async (bb) => {
    const next = bb.status === "active" ? "inactive" : "active";
    try {
      await api.post(`/buybox/${bb.id}/status`, { status: next });
      toast.success(next === "active" ? "Buy Box activated" : "Buy Box deactivated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const duplicate = async (bb) => {
    try {
      await api.post(`/buybox/${bb.id}/duplicate`);
      toast.success("Buy Box duplicated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/buybox/${toDelete.id}`);
      toast.success("Buy Box deleted");
      setToDelete(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const markets = (bb) => {
    const g = bb.geography || {};
    const parts = [...(g.cities || []), ...(g.counties || [])];
    return parts.length ? parts.slice(0, 3).join(" · ") + (parts.length > 3 ? ` +${parts.length - 3}` : "") : (g.state || "—");
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="my-buy-boxes-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <Boxes className="h-4 w-4" /> Operations
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">My Buy Boxes</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Save acquisition criteria by market and share public links with wholesalers and agents.
          </p>
        </div>
        <Button data-testid="create-buy-box-btn" onClick={() => nav("/buybox/new")} className="shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Create Buy Box
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="buy-box-empty">
          <Boxes className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <h3 className="text-lg font-medium">No Buy Boxes yet</h3>
          <p className="text-muted-foreground text-sm mt-1 mb-5">
            Create your first Buy Box to define what you buy and start collecting deals.
          </p>
          <Button data-testid="create-buy-box-empty-btn" onClick={() => nav("/buybox/new")}>
            <Plus className="h-4 w-4 mr-1.5" /> Create Buy Box
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((bb) => (
            <Card key={bb.id} onClick={() => nav(`/buybox/${bb.id}/edit`)} className="p-5 hover:shadow-md transition-shadow cursor-pointer" data-testid={`buy-box-card-${bb.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-lg truncate">{bb.name || "Untitled Buy Box"}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                    <MapPin className="h-3.5 w-3.5" /> {markets(bb)}
                  </div>
                </div>
                <Badge variant="outline" className={STATUS_STYLE[bb.status] || STATUS_STYLE.draft}>
                  {bb.status || "draft"}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="h-3 w-3" /> Max Price</div>
                  <div className="font-medium">{money(bb.criteria?.max_price)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><Inbox className="h-3 w-3" /> Submissions</div>
                  <div className="font-medium">{bb.submissions_count ?? 0}</div>
                </div>
              </div>

              {(bb.property_types || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(bb.property_types || []).slice(0, 3).map((t) => (
                    <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{t}</span>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 mt-5" onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="outline" className="flex-1" data-testid={`buy-box-submissions-${bb.id}`} onClick={() => nav(`/buybox/${bb.id}/submissions`)}>
                  <Inbox className="h-3.5 w-3.5 mr-1.5" /> Submissions
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid={`buy-box-menu-${bb.id}`}>•••</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem data-testid={`buy-box-copylink-${bb.id}`} onClick={() => copyLink(bb)}>
                      <Link2 className="h-4 w-4 mr-2" /> Copy Link
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => window.open(publicUrl(bb.slug), "_blank")}>
                      <Eye className="h-4 w-4 mr-2" /> Preview Public Page
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => duplicate(bb)}>
                      <Copy className="h-4 w-4 mr-2" /> Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => toggleStatus(bb)}>
                      <Power className="h-4 w-4 mr-2" /> {bb.status === "active" ? "Deactivate" : "Activate"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-red-600" onClick={() => setToDelete(bb)}>
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Buy Box?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.name}" will be permanently removed. Submitted properties already in Property Screening are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-buy-box" onClick={doDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
